/**
 * ContentEditableInput hook for rich mention rendering with plain text input.
 *
 * Features:
 * - Contenteditable div for custom rendering
 * - Strips formatting on paste (plain text only)
 * - Auto-expands height
 * - Syncs content to hidden input for form submission
 * - Converts mention spans to markdown before submission
 */
export default {
  mounted() {
    this.editor = this.el;
    this.hiddenInput = document.getElementById('message-input-value');
    this.placeholder = this.editor.dataset.placeholder;
    this.form = this.editor.closest('form'); // Find parent form

    if (!this.hiddenInput) {
      console.error('[ContentEditableInput] Hidden input not found');
      return;
    }

    if (!this.form) {
      console.error('[ContentEditableInput] Form not found');
      return;
    }

    // Bind updatePlaceholder so we can use it with MutationObserver
    this.boundUpdatePlaceholder = this.updatePlaceholder.bind(this);

    // Handle paste - strip all formatting
    this.editor.addEventListener('paste', (e) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
      this.boundUpdatePlaceholder();
    });

    // Handle input to update hidden field and adjust height
    this.editor.addEventListener('input', () => {
      this.syncToHiddenInput();
      this.boundUpdatePlaceholder();
    });

    // Handle blur to update placeholder state
    this.editor.addEventListener('blur', () => {
      this.boundUpdatePlaceholder();
    });

    // Handle focus
    this.editor.addEventListener('focus', () => {
      this.boundUpdatePlaceholder();
    });

    // Watch for any DOM changes to contenteditable
    this.observer = new MutationObserver(() => {
      this.boundUpdatePlaceholder();
    });

    this.observer.observe(this.editor, {
      childList: true,
      characterData: true,
      subtree: true
    });

    // Handle form submission - convert mentions to markdown
    // Use capture phase to ensure we sync BEFORE LiveView reads the form
    this.submitHandler = (e) => {
      this.syncToHiddenInput();

      // Clear editor after form submission
      // Use a short delay to ensure LiveView has captured the form data
      setTimeout(() => {
        this.clear();
      }, 50);
    };

    if (this.form) {
      this.form.addEventListener('submit', this.submitHandler, true); // Capture phase
    }

    // Handle keyboard shortcuts
    this.editor.addEventListener('keydown', (e) => {
      // Handle Enter key
      if (e.key === 'Enter') {
        // Skip while an IME (e.g. Japanese/Chinese/Korean) is composing —
        // Enter is used to confirm the conversion, not submit the message.
        if (e.isComposing || e.keyCode === 229) {
          return;
        }
        if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) {
          // Shift/Ctrl/Alt/Cmd + Enter: insert newline
          // Allow default behavior (browser will insert <br> or <div>)
          return;
        } else {
          // Plain Enter: submit form
          e.preventDefault();
          e.stopPropagation();
          this.syncToHiddenInput();

          // Find and click the submit button
          const submitButton = this.form.querySelector('button[type="submit"]');

          if (submitButton) {
            submitButton.click();

            // Clear editor after a short delay to allow LiveView to process
            setTimeout(() => {
              this.clear();
            }, 50);
          } else {
            // Fallback: dispatch submit event
            this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));

            setTimeout(() => {
              this.clear();
            }, 50);
          }
          return;
        }
      }

      // Prevent bold, italic, etc.
      if (e.metaKey || e.ctrlKey) {
        if (['b', 'i', 'u'].includes(e.key.toLowerCase())) {
          e.preventDefault();
        }
      }
    });

    // Initialize placeholder
    this.boundUpdatePlaceholder();

    // Focus after a short delay (for sandboxed iframes)
    setTimeout(() => {
      this.editor.focus();
    }, 100);

    // Listen for focus event from server
    this.handleEvent('focus_input', () => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        this.editor.focus();
      });
    });

    // Listen for clear event from server (e.g., when creating new chat)
    this.handleEvent('clear_input', () => {
      this.clear();
    });
  },

  /**
   * Syncs contenteditable content to hidden input, converting mentions to markdown.
   */
  syncToHiddenInput() {
    const content = this.extractMarkdownContent();
    this.hiddenInput.value = content;
  },

  /**
   * Extracts content from contenteditable and converts mentions to markdown.
   */
  extractMarkdownContent() {
    let markdown = '';

    // Walk through all child nodes
    for (const node of this.editor.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        markdown += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList.contains('mention')) {
          // Mention spans are tracked as separate attachments, so skip them
          // in the text content to avoid showing raw @[Title](tab:id) in messages
        } else if (node.tagName === 'BR') {
          markdown += '\n';
        } else if (node.tagName === 'DIV') {
          // Handle divs created by contenteditable for newlines
          markdown += '\n' + this.extractTextSkippingMentions(node);
        } else {
          // Fallback to text content (skip any nested mentions)
          markdown += this.extractTextSkippingMentions(node);
        }
      }
    }

    return markdown;
  },

  /**
   * Extracts text content from a node, skipping mention spans.
   */
  extractTextSkippingMentions(node) {
    let text = '';
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.classList && child.classList.contains('mention')) {
          // Skip mention spans
        } else {
          text += this.extractTextSkippingMentions(child);
        }
      }
    }
    return text;
  },

  /**
   * Shows/hides placeholder based on content.
   */
  updatePlaceholder() {
    const text = this.editor.textContent.trim();
    const hasOnlyBr = this.editor.innerHTML === '<br>' || this.editor.innerHTML === '';
    const isEmpty = text === '' || hasOnlyBr;

    if (isEmpty) {
      this.editor.setAttribute('data-empty', 'true');
    } else {
      this.editor.removeAttribute('data-empty');
    }
  },

  /**
   * Clears the editor content.
   */
  clear() {
    this.editor.innerHTML = '';
    this.syncToHiddenInput();
    this.updatePlaceholder();
  },

  destroyed() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.form && this.submitHandler) {
      this.form.removeEventListener('submit', this.submitHandler, true);
    }
  }
};
